import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import GlossaryCard from '../components/knowledge/GlossaryCard';
import { glossaryService, type GlossaryTermSummary } from '../services/glossaryService';

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const DIFFICULTIES = ['starter', 'intermediate', 'advanced'] as const;

const DIFFICULTY_STYLES: Record<string, string> = {
  starter: 'text-teal-400 border-teal-500/50 bg-teal-500/10',
  intermediate: 'text-amber-400 border-amber-500/50 bg-amber-500/10',
  advanced: 'text-red-400 border-red-500/50 bg-red-500/10',
};

export default function GlossaryPage() {
  const navigate = useNavigate();
  const [terms, setTerms] = useState<GlossaryTermSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeLetter, setActiveLetter] = useState('');
  const [activeDifficulty, setActiveDifficulty] = useState('');

  useEffect(() => {
    glossaryService.listPublic()
      .then(setTerms)
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return terms.filter(t => {
      if (activeLetter && !t.term.toUpperCase().startsWith(activeLetter)) return false;
      if (activeDifficulty && t.difficulty_hint !== activeDifficulty) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!t.term.toLowerCase().includes(q) && !t.definition.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [terms, search, activeLetter, activeDifficulty]);

  const grouped = useMemo(() => {
    const g: Record<string, GlossaryTermSummary[]> = {};
    for (const t of filtered) {
      const l = t.term[0]?.toUpperCase() ?? '#';
      (g[l] = g[l] || []).push(t);
    }
    return g;
  }, [filtered]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
        {/* Search + difficulty filter */}
        <div className="mb-6 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search terms or definitions…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {DIFFICULTIES.map(d => (
              <button
                key={d}
                onClick={() => setActiveDifficulty(activeDifficulty === d ? '' : d)}
                className={`px-3 py-1.5 rounded-lg text-xs border capitalize transition-colors ${
                  activeDifficulty === d
                    ? DIFFICULTY_STYLES[d]
                    : 'text-slate-400 border-slate-600 hover:border-slate-500'
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>

        {/* A-Z jump bar */}
        <div className="flex flex-wrap gap-1 mb-6">
          {ALPHABET.map(l => {
            const hasTerms = terms.some(t => t.term.toUpperCase().startsWith(l));
            return (
              <a
                key={l}
                href={`#letter-${l}`}
                onClick={e => { e.preventDefault(); setActiveLetter(activeLetter === l ? '' : l); }}
                className={`w-7 h-7 flex items-center justify-center text-xs rounded transition-colors ${
                  activeLetter === l
                    ? 'bg-teal-500/20 text-teal-400'
                    : hasTerms
                    ? 'text-slate-300 hover:bg-slate-700/50'
                    : 'text-slate-600 cursor-default'
                }`}
              >
                {l}
              </a>
            );
          })}
        </div>

        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="rounded-xl bg-slate-800/70 border border-slate-700/50 p-4 animate-pulse">
                <div className="h-4 bg-slate-700/50 rounded w-3/4 mb-3" />
                <div className="h-3 bg-slate-700/50 rounded w-full mb-1.5" />
                <div className="h-3 bg-slate-700/50 rounded w-5/6" />
              </div>
            ))}
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <p className="text-slate-500 text-sm text-center py-16">No terms found.</p>
        )}

        {!loading && Object.keys(grouped).sort().map(letter => (
          <section key={letter} id={`letter-${letter}`} className="mb-8">
            <h2 className="text-amber-400 text-lg font-bold mb-3">{letter}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {grouped[letter].map(t => (
                <GlossaryCard
                  key={t.slug}
                  term={t}
                  onClick={() => navigate(`/knowledge/glossary/${t.slug}`)}
                />
              ))}
            </div>
          </section>
        ))}
    </div>
  );
}
